import widgetGlobalStyles from 'bundle-text:../../../public/widget-global.css';
import indexStyles from 'bundle-text:../../index.css';
import { publicFonts } from '../../global-fonts';

export interface CreateShadowRootOptions {
	includeIndexStyles?: boolean;
	includeFonts?: boolean;
	extraCss?: string;
}

// Shared stylesheet cache — CSS is parsed once and adopted by all Shadow Roots.
// Avoids duplicating <style> nodes and re-parsing the same CSS per widget instance.
const sharedSheetCache = new Map<string, CSSStyleSheet>();
async function getSharedSheet(css: string, cacheKey: string): Promise<CSSStyleSheet> {
	const cachedSheet = sharedSheetCache.get(cacheKey);
	if (cachedSheet) {
		return cachedSheet;
	}

	const sheet = new CSSStyleSheet();
	// Wrap inside @layer so all Tailwind utilities (including responsive @media variants)
	// are layered CSS. This guarantees unlayered custom CSS in <style> tags always wins,
	// regardless of adoptedStyleSheets being last in the Shadow DOM cascade.
	await sheet.replace(`@layer widget-global { ${css} }`);

	sharedSheetCache.set(cacheKey, sheet);

	return sheet;
}

// Los iconos de kit de FontAwesome (`fa-kit`) se pintan como glifo de una fuente, gobernado por la
// hoja del kit, no como SVG en linea como los que el design system tiene mapeados. Esa hoja vive en
// el documento y el Shadow DOM no la hereda: un icono que el administrador configuraba resolvia al
// <i> correcto con la clase correcta y aun asi medía 0x0. Se replican sus nodos dentro de cada
// Shadow Root. El @font-face se queda ademas en el documento, que es de donde el navegador lo
// descarga de forma fiable.
// mirroredShadowRoots queda acotado al numero de widgets montados: createShadowRoot reutiliza el
// shadow root del host, no crea uno por render.
const FONT_AWESOME_KIT_ORIGIN = 'fontawesome.com';
const KIT_MIRROR_ATTRIBUTE = 'data-fa-kit-mirror';
const mirroredShadowRoots = new Set<ShadowRoot>();
let kitStyleObserver: MutationObserver | null = null;

function isFontAwesomeKitStyleNode(node: Element): boolean {
	if (node instanceof HTMLLinkElement) {
		return node.rel === 'stylesheet' && node.href.includes(FONT_AWESOME_KIT_ORIGIN);
	}

	return node.tagName === 'STYLE' && /\.fa-kit|\.fak\b/.test(node.textContent ?? '');
}

function mirrorFontAwesomeKitStyles(shadow: ShadowRoot): number {
	shadow.querySelectorAll(`[${KIT_MIRROR_ATTRIBUTE}]`).forEach(previous => previous.remove());

	let mirrored: number = 0;
	document.querySelectorAll('link[rel="stylesheet"], style').forEach(node => {
		if (!isFontAwesomeKitStyleNode(node)) {
			return;
		}

		const mirror = node.cloneNode(true) as HTMLElement;
		mirror.setAttribute(KIT_MIRROR_ATTRIBUTE, '');

		shadow.appendChild(mirror);

		mirrored += 1;
	});

	return mirrored;
}

// El kit se carga por script y puede llegar despues de que el widget monte; sin esto, el primer
// render se queda sin la hoja y el icono no aparece hasta recargar.
function watchFontAwesomeKitStyles(): void {
	if (kitStyleObserver || typeof MutationObserver === 'undefined') {
		return;
	}

	kitStyleObserver = new MutationObserver(mutations => {
		// El disparador es cualquier <style> o <link>, no uno que ya contenga reglas del kit: el
		// kit inserta el nodo vacio y le pone el CSS justo despues, asi que al momento de la
		// insercion todavia no se reconoceria como suyo.
		const styleArrived: boolean = mutations.some(mutation =>
			Array.from(mutation.addedNodes).some(node => node instanceof Element && (node.tagName === 'STYLE' || node.tagName === 'LINK')),
		);

		if (!styleArrived) {
			return;
		}

		let mirrored: number = 0;
		mirroredShadowRoots.forEach(root => {
			mirrored += mirrorFontAwesomeKitStyles(root);
		});

		// La hoja del kit entra una sola vez; replicada ya, no queda nada que vigilar.
		if (mirrored > 0) {
			kitStyleObserver?.disconnect();

			kitStyleObserver = null;
		}
	});

	// No basta con vigilar document.head: hay marketplaces donde el kit inyecta un <style> en el
	// body, y ahi el espejo nunca se disparaba. El icono resolvia a su clase correcta y se
	// quedaba en 0x0, sin glifo, que es justo el sintoma que se veia en BAC.
	kitStyleObserver.observe(document.documentElement, { childList: true, subtree: true });
}

export class CommonsUtil {
	public static async createShadowRoot(hostElement: HTMLDivElement, options?: CreateShadowRootOptions): Promise<HTMLDivElement> {
		const shadow: ShadowRoot = hostElement.shadowRoot ?? hostElement.attachShadow({ mode: 'open' });
		const cssParts: string[] = [widgetGlobalStyles?.toString() || ''];
		if (options?.includeIndexStyles) {
			cssParts.push(indexStyles?.toString() || '');
		}

		// Build a deterministic cache key from options (extraCss varies per widget).
		const cacheKey = `${options?.includeIndexStyles ? '1' : '0'}${options?.includeFonts ? '1' : '0'}`;
		const sharedCss = cssParts.join('\n');
		const adoptedSheets: CSSStyleSheet[] = [await getSharedSheet(sharedCss, cacheKey)];
		// @font-face declared in document.head so browsers reliably trigger font downloads.
		if (options?.includeFonts && document.querySelector('style[data-description="smartlinks-fonts"]') == null) {
			const style: HTMLStyleElement = document.createElement('style');
			style.dataset.description = 'smartlinks-fonts';

			style.appendChild(document.createTextNode(publicFonts || ''));

			document.head.appendChild(style);
		}

		// extraCss is per-widget and typically small — inject as a separate <style> to avoid
		// cache fragmentation on the shared base sheets.
		if (options?.extraCss) {
			const extraSheet = new CSSStyleSheet();
			await extraSheet.replace(options.extraCss);

			adoptedSheets.push(extraSheet);
		}

		// Clear right before mounting to avoid races when the same widget is initialized concurrently.
		shadow.innerHTML = '';

		shadow.adoptedStyleSheets = adoptedSheets;

		mirroredShadowRoots.add(shadow);

		mirrorFontAwesomeKitStyles(shadow);

		watchFontAwesomeKitStyles();

		const renderIn: HTMLDivElement = document.createElement('div');
		shadow.appendChild(renderIn);

		return renderIn;
	}

	public static debounce(callback: () => void, wait: number, timeoutId: number) {
		return (...args: []) => {
			window.clearTimeout(timeoutId || 0);

			return window.setTimeout(() => {
				callback.apply(null, args);
			}, wait);
		};
	}

	static verifyWidgetElement(elementId?: string): HTMLDivElement | null {
		const getElement: HTMLDivElement | null = elementId ? (document.getElementById(elementId) as HTMLDivElement) : null;
		if (!getElement) {
			const noFoundErroMsg: string = 'elementNofound';
			console.error(noFoundErroMsg);
		}

		return getElement;
	}

	public static readonly capitalize = (text: string): string => {
		return text
			? text
				.split(' ')
				.map((word: string) => {
					const lower = word.toLowerCase();
					return word.charAt(0).toUpperCase() + lower.slice(1);
				})
				.join(' ')
			: text;
	};
}
